import React from 'react';
import { DataGrid, DataGridProps, GridColDef } from '@mui/x-data-grid';
import { makeStyles } from 'tss-react/mui';
import { TableColumn } from './Table.types';
import { LoadingOverlay, NoItemsOverlay, OverlayProps } from './Overlay';
import { useInfiniteScrollHandler } from './Table.hooks';


export type TableProps<Row extends Record<string, unknown>> = Omit<DataGridProps, 'columns' | 'rows'> & {
  columns: (TableColumn<Row, keyof Row> & { id?: string })[];
  rows: Row[];
  emptyMessage?: OverlayProps['message'];
  emptyIcon?: OverlayProps['icon'];
  onInfiniteScroll?: () => void;
};


// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useTableStyles = makeStyles()(theme => ({
  root: {
    border: 0,
    minHeight: '400px',
  },
  cell: {
    color: theme.palette.grey[900],
    outline: 'none !important',
    '& > *': {
      lineHeight: '1.2em',
    }
  },
  columnHeader: {
    color: theme.palette.grey[900],
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    }
  },
}));


export function Table<
  Row extends Record<string, unknown>
>({ columns, rows, onInfiniteScroll, ...props }: TableProps<Row>) {
  const { setScrollRef } = useInfiniteScrollHandler(onInfiniteScroll);
  const { classes } = useTableStyles();

  const overlay = React.useCallback(() => (
    <NoItemsOverlay
      icon={props.emptyIcon}
      message={props.emptyMessage}
    />
  ), [ props.emptyIcon, props.emptyMessage ]);

  return (
    <DataGrid
      ref={ref => setScrollRef(ref)}
      pageSizeOptions={[ 10, 20, 30 ]}
      {...props}
      columns={columns as unknown as GridColDef[]}
      rows={rows as unknown as Row[]}
      classes={classes}
      slots={{
        noRowsOverlay: overlay,
        loadingOverlay: LoadingOverlay
      }}
    />
  );
}
